import uniqBy from 'lodash/uniqBy';
import cloneDeep from 'lodash/cloneDeep';
import reject from 'lodash/reject';
import { AggregateItemSelector, AppAggregateItemSelector } from './aggregate-item-selector';
import {
  SelectableMenuItem,
  MealBuilderItemSection,
  SelectableVariantMenuItem,
  ItemCardCustomizationType,
  SelectableMenuItemSource,
  ItemCardCustomization,
  MenuItemCardCustomization,
  ItemCardSelectionInfoKind,
  SelectableItemCardCustomization,
  MenuItem,
} from '@/models';
import {
  hasMaxContents,
  getCountTowardsCustomizationMax as countTowardsCustomizationMax,
  getNumberOfSelectedCustomizations as numSelectedCustomizations,
  getNumberOfSelectedOnTheSideCustomizations as numSelectedOnTheSideCustomizations,
  getCountTowardsOnTheSideCustomizationMax as countTowardsOnTheSideCustomizationMax
} from '@/services/meal-builder/utils';
import { makeUniqueValue, UniqueValue } from '@/helpers';
import { MUTATIONS } from '@/store/meal-builder';
import { ErrorEvent } from '@/services/analytics/events';

export interface MealBuilderItemSelector extends AggregateItemSelector {
  selectItemSafe<TArgs>(
    item: SelectableMenuItem,
    itemSection: MealBuilderItemSection | null,
    variant: SelectableVariantMenuItem | null,
    args: TArgs,
    showInformationalMessage?: boolean,
    shouldTrackAnalytics?: boolean,
  ): void;

  toggleItemCustomizationSafe(
    item: SelectableMenuItem,
    customization: ItemCardCustomization,
    itemSection: MealBuilderItemSection | null,
    variant?: SelectableVariantMenuItem,
    showInformationalMessage?: boolean,
    shouldTrackAnalytics?: boolean,
  ): void;

  optOutOfDefaultContentItem(itemId: string): void;
  toggleOptOutOfItemSection(itemSection: MealBuilderItemSection, shouldTrackAnalytics?: boolean): void;
  optOutOfItemSection(itemSection: MealBuilderItemSection, shouldTrackAnalytics?: boolean): void;
  removeOptOutOfItemSection(itemSection: MealBuilderItemSection, shouldTrackAnalytics?: boolean): void;

  getEffectiveItemSectionQuantity(itemSection: MealBuilderItemSection): number;
}

export class AppMealBuilderItemSelector extends AppAggregateItemSelector implements MealBuilderItemSelector {
  selectItemSafe<TArgs>(
    item: SelectableMenuItem,
    itemSection: MealBuilderItemSection | null,
    variant: SelectableVariantMenuItem | null | undefined,
    args: TArgs,
    showInformationalMessage: boolean = true,
    shouldTrackAnalytics: boolean = true,
  ): void {
    // Based on other methods calling this function we may have already tracked analytic
    // events in those precursor calls and do not want to duplicate them in this method.
    const callTrackAnalytics = (selectableMenuItem: SelectableMenuItem | null, actionSelected: 'add' | 'remove' | 'portion' | 'customize', actionValue?: string): void => {
      if (shouldTrackAnalytics && itemSection) {
        this.mealBuilderAnalytics.trackItemAnalytics(itemSection.title, selectableMenuItem, variant, actionSelected, actionValue);
      }
    };

    const numCustomizations = numSelectedCustomizations(this.getSelectedItems(variant?.contents ?? []));

    // If the user wants to deselect the item, always allow them to do it:
    if (this.isDeselecting(item, args)) {
      // If the section has a minimum required rule and the section only has a number of items equal to that 
      // minimum requirement (e.g. BYO Chips Included Sides), do not allow the customer to deselect an item and force themselves into an error state
      if (itemSection && itemSection.items.length <= (itemSection.minQuantity ?? 0)) {
        item.message = makeUniqueValue(`Always included`);
        this.analytics.send(new ErrorEvent('builder', item.message.value, 'item-selection'));
        return;
      }

      this.logger.debug('Deselecting item', { item: item.item.itemId });

      this.deselectItem(item);
      this.deselectAllItemCustomizations(item, false);

      callTrackAnalytics(item, 'remove');

      // HACK: this should be generalized for any case where we have special countTowardsCustomizationMax values
      // If this was a half-and-half scenario, deselect the remaining item's customization to bring it back to full quantity
      if (variant && itemSection) {
        const selectedItemsInSection = this.getSelectedItems(itemSection.items);

        if (selectedItemsInSection.length == 1) {
          const remainingItem = selectedItemsInSection[0];
          const customization = remainingItem.customizations.find(c => c.selected);

          // Undo any content-related customizations
          if (customization?.type == ItemCardCustomizationType.Customization && customization.customization.countTowardsContentMax === -0.5) {
            customization.selected = false;
            callTrackAnalytics(remainingItem, 'customize', 'normal');

            if (showInformationalMessage) {
              this.addNumCustomizationsRemainingMessage(item, variant, numCustomizations - 1);
            }

            return;
          }
        }
      }

      return;
    }

    const selectedItemsInSection = itemSection?.items.filter(i => this.isItemSelected(i));

    const noneArray: string[] = ['CMG-5254', 'CMG-5556', 'CMG-5003', 'CMG-5053'];
    if(selectedItemsInSection !== undefined && selectedItemsInSection.length === 1 && noneArray.includes(selectedItemsInSection[0].item.itemId)){
      this.deselectItem(selectedItemsInSection[0]);
    }

    if (
      item.selectionInfo.kind != ItemCardSelectionInfoKind.Quantity &&
      item.groupedItemSelectionOptions &&
      selectedItemsInSection?.some(i => i.item.itemId === item.item.itemId)
    ) {
      // TODO tech debt - refactor all of meal builder to handle grouped items "selecting twice" on customization
      // Bail out if we're trying to select a grouped item that's already selected and it is not a Quanity type (like
      // Tractor bevs).
      return;
    }

    // Make sure we can add an additional customization to the variant
    if (
      variant &&
      variant.item.maxCustomizations !== -1 &&
      item.item.countTowardsCustomizationMax &&
      numCustomizations + item.item.countTowardsCustomizationMax > variant.item.maxCustomizations
    ) {
      this.logger.debug('Attempted to add more items to customizations than allowed by variant', { variant: variant.item.itemId });
      item.message = makeUniqueValue(`Max of ${variant.item.maxCustomizations} customizations per meal`);
      this.analytics.send(new ErrorEvent('builder', item.message.value, 'item-selection'));
      return;
    }

    const quantityAfterSelection = this.getQuantityAfterSelection(item, args);

    // Make sure we can add an additional item to the group:
    if (itemSection?.maxQuantity != null) {
      const selectedItemsInSection = itemSection.items.filter(i => this.isItemSelected(i));
      const sectionQuantity = this.getEffectiveItemsQuantity(selectedItemsInSection);

      if (sectionQuantity + quantityAfterSelection > itemSection.maxQuantity) {
        // If there's only one other selected item and we have customizations available to make the item selection work, do it
        const numSelectedItems = selectedItemsInSection.length;
        if (variant && numSelectedItems === 1 && item.customizations.length) {
          const otherItem = selectedItemsInSection[0];
          if (otherItem.item.countTowardsContentMax && otherItem.item.countTowardsContentMax < 2) {
            // This fancy splitting logic only works when the existing item's countTowardsContentMax is less than 2, which is currently every item but dang CMG-1406, double cauli rice for Kid's BYO
            return this.selectItemBySplittingSection(item, otherItem, variant, itemSection, numCustomizations, args, showInformationalMessage, shouldTrackAnalytics);
          }
        }

        // If there's only one item allowed by the section, just switch to that new item
        if (itemSection.maxQuantity === 1 && selectedItemsInSection.length === 1) {
          const otherItem = selectedItemsInSection[0];

          this.deselectItemShallow(otherItem);
          this.selectItemShallow(item, args);

          this.synchronizeItems([item, otherItem]);

          callTrackAnalytics(otherItem, 'remove');
          callTrackAnalytics(item, 'add');
          return;
        }

        // special case for half and half selections in a limit 1 group.
        if (itemSection.maxQuantity === 1 && selectedItemsInSection.length > 1) {
          item.message = makeUniqueValue(`You can only choose up to 1 portion of ${itemSection.title}`);
          this.analytics.send(new ErrorEvent('builder', item.message.value, 'item-selection'));
          return;
        }

        item.message = this.getItemMaxQuantityMessage(variant, itemSection);
        this.analytics.send(new ErrorEvent('builder', item.message.value, 'item-selection'));
        return;
      }
    }

    // Make sure we can add an additional item to the variant
    if (variant && item.itemSource == SelectableMenuItemSource.VariantContents) {
      // Let's ignore premium add ons, etc. These items with a countTowardsContentMax of 0 should
      // not be included in our limit for the max number of contents in an entree variant
      const contentsWithCountTowardsMax = variant.contents.filter(c => !!c.item.countTowardsContentMax && c.item.countTowardsContentMax > 0);
      const numSelectedContents = variant ? this.getSelectedItems(contentsWithCountTowardsMax).length + 1 : 0;

      if (hasMaxContents(variant.item) && numSelectedContents > variant.item.maxContents) {
        item.message = makeUniqueValue(`You can choose up to ${variant.item.maxContents} items`);
        return;
      }
    }

    if (quantityAfterSelection > this.getMaxQuantity(item)) {
      this.logger.debug('Attempted to increase quantity to value higher than allowed by item max quantity', { item: item.item.itemId });
      return;
    }

    this.logger.debug('Selecting item', { item: item.item.itemId });
    if (item.selectionInfo.kind === ItemCardSelectionInfoKind.Quantity && Number(item.selectionInfo.quantity) > Number(args)) {
      callTrackAnalytics(item, 'remove');
    } else {
      callTrackAnalytics(item, 'add');
    }

    this.selectItem(item, args);

    if (itemSection) {
      this.validateMissingSection(itemSection.title);
      this.removeOptOutOfItemSection(itemSection, shouldTrackAnalytics);
    }

    if (item.item.countTowardsCustomizationMax && variant) {
      if (showInformationalMessage) {
        this.addNumCustomizationsRemainingMessage(item, variant, numCustomizations);
      }
    }
  }
  
  private getItemMaxQuantityMessage(variant: SelectableVariantMenuItem | null | undefined, itemSection: MealBuilderItemSection<MenuItem>): UniqueValue<string> {

    switch(variant?.item.itemType) {
      case 'BYOProtein':  
        return makeUniqueValue(`Limited to ${itemSection.maxQuantity} sides`);
      default: 
        return  makeUniqueValue(`You can choose up to ${itemSection.maxQuantity} ${itemSection.title}`);
    }
  }

  swapGroupedItem(
    customization: SelectableItemCardCustomization,
    itemSection: MealBuilderItemSection<MenuItem> | null,
    variant: SelectableVariantMenuItem | undefined,
    showInformationalMessage: boolean,
  ): void {
    try {
      const groupedCustomization = customization.customization;
      if (groupedCustomization && groupedCustomization.id != customization.item.item.itemId) {
        const groupedItemForCardSwap = cloneDeep(customization.item.item);
        groupedItemForCardSwap.itemId = groupedCustomization.id;
        groupedItemForCardSwap.itemName = groupedCustomization.name;
        groupedItemForCardSwap.posId = groupedCustomization.posId;
        groupedItemForCardSwap.unitPrice = groupedCustomization.unitPrice;
        groupedItemForCardSwap.unitDeliveryPrice = groupedCustomization.unitDeliveryPrice;
        groupedItemForCardSwap.countTowardsCustomizationMax = groupedCustomization.countTowardsCustomizationMax;
        groupedItemForCardSwap.countTowardsContentMax = groupedCustomization.countTowardsContentMax;
        customization.item.item = groupedItemForCardSwap;
      }
      
      // This conditional is terrible and this logic really shouldn't live here. However, this is an edge case being introduced by the new meal builder variant work
      // to test a QOL improvement when swapping out drinks. When the control / variant logic is removed, we will just switch to this new path.
      if (customization.item.selectionInfo.kind === ItemCardSelectionInfoKind.Quantity && 
          this.store.state.adobeTarget.mealBuilderMobileCustomizationsExperienceEnabled && 
          this.store.state.ui.clientWidth < 1024) { 
        this.selectItemSafe(customization.item, itemSection, variant, customization.item.selectionInfo.quantity, showInformationalMessage, false);
      } else { 
        this.selectItemSafe(customization.item, itemSection, variant, 1, showInformationalMessage, false);
      }

      customization.item.customizations.forEach(i => (i.selected = false));
      customization.item.item.customizations = undefined;
    } catch (e: any) {
      return;
    }
  }

  // `variant` is optional to handle Sides & Drinks standalone menu - which is a Zero Variant meal builder
  toggleItemCustomizationSafe(
    item: SelectableMenuItem,
    customization: ItemCardCustomization,
    itemSection: MealBuilderItemSection | null,
    variant?: SelectableVariantMenuItem,
    showInformationalMessage: boolean = true,
    shouldTrackAnalytics: boolean = true,
  ): void {
    // Based on other methods calling this function we may have already tracked analytic
    // events in those precursor calls and do not want to duplicate them in this method.
    const callTrackAnalytics = (selectableMenuItem: SelectableMenuItem | null, actionSelected: 'add' | 'remove' | 'portion' | 'customize', actionValue?: string): void => {
      if (shouldTrackAnalytics && itemSection) {
        this.mealBuilderAnalytics.trackItemAnalytics(itemSection.title, selectableMenuItem, variant, actionSelected, actionValue);
      }
    };

    const selecting = customization && !customization.selected;

    // Make sure we can actually change the customization and maintain our allowed content count
    const itemSectionMaxQuantity = itemSection?.maxQuantity;
    let selectedItemsInSection = this.getSelectedItems(itemSection?.items ?? []);

    // If a grouped item is already selected that we're trying to select a customization for, we must discount its count towards content max UNLESS that selection is half/half because it spans multiple items
    const currentCustomization = item.customizations.find(c => c.selected && c.type == ItemCardCustomizationType.Customization) as MenuItemCardCustomization;
    if (item.groupedItemSelectionOptions && currentCustomization?.customization.id !== 4 && selectedItemsInSection.some(i => i.item.itemId === item.item.itemId)) {
      selectedItemsInSection = reject(selectedItemsInSection, i => i.item.itemId === item.item.itemId);
    }
    const currentItemSectionQuantity = this.getEffectiveItemsQuantity(selectedItemsInSection);

    const selectedItems = this.getAllSelectedItems();
    const countTowardsCustomizationMaxForCustomization = countTowardsCustomizationMax(customization);
    const countTowardsOnTheSideCustomizationMaxForCustomization = countTowardsOnTheSideCustomizationMax(customization);
    let numSelectedCustomizationsForVariant = variant && numSelectedCustomizations(selectedItems.concat([variant]));
    let numSelectedOnTheSideCustomizationsForVariant = variant && numSelectedOnTheSideCustomizations(selectedItems.concat([variant]))

    if (itemSectionMaxQuantity != null) {
      const currentCustomizationCountTowardsContentMax = currentCustomization?.customization.countTowardsContentMax ?? 0;

      if (currentItemSectionQuantity - currentCustomizationCountTowardsContentMax > itemSectionMaxQuantity) {
        const error = 'Clearing item customization would cause item section to exceed max quantity';

        this.logger.debug(error);
        item.message = makeUniqueValue(`No customizations left for ${itemSection!.title}`);
        return;
      }

      let countTowardsContentMax;
      if (customization.type == ItemCardCustomizationType.Customization) {
        countTowardsContentMax = customization.customization.countTowardsContentMax;
      } else if (item.groupedItemSelectionOptions && customization) {
        countTowardsContentMax = item.groupedItemSelectionOptions.find(i => i.id === (customization as SelectableItemCardCustomization).customization?.id)
          ?.countTowardsContentMax;
      }

      if (itemSection && selecting && countTowardsContentMax && currentItemSectionQuantity + countTowardsContentMax > itemSectionMaxQuantity) {
        const error = 'Selecting item customization would cause item section to exceed max quantity';

        this.logger.debug(error);
        item.message = makeUniqueValue(`You can choose up to ${itemSection.maxQuantity} ${itemSection.title}`);
        return;
      }
    }

    // If the type is default it's implied we should disable all customizations
    if (
      customization.type === ItemCardCustomizationType.Normal ||
      (customization.type === ItemCardCustomizationType.SelectableItem && customization.customization && customization.customization.id === customization.item.defaultItemId) // is selecting Normal for a grouped item
    ) {
      this.logger.debug('Clearing item customizations', { itemId: item.item.itemId });

      if (customization.type === ItemCardCustomizationType.SelectableItem && customization.item.groupedItemSelectionOptions) {
        this.swapGroupedItem(customization, itemSection, variant, showInformationalMessage);
      } else {
        this.deselectAllItemCustomizations(item, false);
      }
      customization.selected = true;

      if (showInformationalMessage && variant && numSelectedCustomizationsForVariant) {
        this.addNumCustomizationsRemainingMessage(item, variant, numSelectedCustomizationsForVariant);
      }

      callTrackAnalytics(item, 'customize', customization.text);
      return;
    }

    // Clear all customizations and recalculate number of selected customizations
    this.deselectAllItemCustomizations(item, false);
    numSelectedCustomizationsForVariant = variant && numSelectedCustomizations(selectedItems.concat([variant]));

    // If we're deselecting this customization, just bail out!
    if (!selecting) {
      if (customization.type == ItemCardCustomizationType.SelectableItem) {
        this.deselectItem(customization.item);
      }

      if (showInformationalMessage && variant && numSelectedCustomizationsForVariant) {
        this.addNumCustomizationsRemainingMessage(item, variant, numSelectedCustomizationsForVariant);
      }

      callTrackAnalytics(item, 'customize');
      return;
    }

    const maxCustomizations = variant?.item.maxCustomizations;
    if (
      maxCustomizations !== undefined &&
      maxCustomizations !== -1 &&
      numSelectedCustomizationsForVariant !== undefined &&
      countTowardsCustomizationMaxForCustomization + numSelectedCustomizationsForVariant > maxCustomizations
    ) {
      this.logger.debug(`Attempted to add customization that would exceed max customization limit of ${maxCustomizations}`);

      const error = `Max of ${maxCustomizations} customizations per meal`;
      item.message = makeUniqueValue(error);
      this.analytics.send(new ErrorEvent('builder', item.message.value, 'item-selection'));
      this.deselectItem(item);
      return;
    }

    const maxOnTheSideCustomizations = variant?.item.maxOnTheSideCustomizations;
    if (
      maxOnTheSideCustomizations !== undefined &&
      maxOnTheSideCustomizations !== -1 &&
      numSelectedOnTheSideCustomizationsForVariant !== undefined &&
      countTowardsOnTheSideCustomizationMaxForCustomization + numSelectedOnTheSideCustomizationsForVariant > maxOnTheSideCustomizations
    ) {
      this.logger.debug(`Attempted to add on the side customization that would exceed max on the side customization limit of ${maxOnTheSideCustomizations}`);

      const error = `Max of ${maxOnTheSideCustomizations} included sides`;
      item.message = makeUniqueValue(error);
      this.analytics.send(new ErrorEvent('builder', item.message.value, 'item-selection'));
      this.deselectItem(item);
      return;
    }

    // Grouped menu items will go into this block
    if (customization.type == ItemCardCustomizationType.SelectableItem) {
      this.swapGroupedItem(customization, itemSection, variant, showInformationalMessage);
    }

    this.logger.debug('Selecting customization for item', { customization: customization.text, item: item.item.itemId });

    customization.selected = true;

    callTrackAnalytics(item, 'customize', customization.text);

    if (showInformationalMessage && variant && numSelectedCustomizationsForVariant) {
      this.addNumCustomizationsRemainingMessage(item, variant, numSelectedCustomizationsForVariant);
    }
  }

  getEffectiveItemSectionQuantity(itemSection: MealBuilderItemSection) {
    const selectedItemsInSection = itemSection.items.filter(i => this.isItemSelected(i));
    return this.getEffectiveItemsQuantity(selectedItemsInSection);
  }

  deselectItemShallow(item: SelectableMenuItem) {
    if (item.item.defaultContent) {
      this.optOutOfDefaultContentItem(item.item.itemId);
    }

    return super.deselectItemShallow(item);
  }

  resetItemSelections() {
    this.setOptedOutDefaultContentItemIds([]);
    this.setOptedOutItemSectionIds([]);

    super.resetItemSelections();
  }

  optOutOfDefaultContentItem(itemId: string) {
    this.setOptedOutDefaultContentItemIds([...this.store.state.mealBuilder.optedOutDefaultContentItemIds, itemId]);
  }

  toggleOptOutOfItemSection(itemSection: MealBuilderItemSection, shouldTrackAnalytics?: boolean) {
    const optedOutSections = this.store.state.mealBuilder.optedOutItemSectionIds;

    if (optedOutSections.includes(itemSection.id)) {
      this.removeOptOutOfItemSection(itemSection, shouldTrackAnalytics);
    } else {
      this.optOutOfItemSection(itemSection, shouldTrackAnalytics);
    }
  }

  optOutOfItemSection(itemSection: MealBuilderItemSection, shouldTrackAnalytics: boolean = true) {
    const optedOutSections = this.store.state.mealBuilder.optedOutItemSectionIds;
    const newlyOptedOutSections = [...optedOutSections, itemSection.id];
    const selectedVariant = this.store.state.mealBuilder.selectedVariant;
    const selectedItems = this.getSelectedItems(itemSection.items);

    this.deselectAllItems(selectedItems);
    this.setOptedOutItemSectionIds(newlyOptedOutSections);

    if (shouldTrackAnalytics) {
      selectedItems.forEach(item => {
        this.mealBuilderAnalytics.trackItemAnalytics(itemSection.title, item, selectedVariant, 'remove');
      });
      this.mealBuilderAnalytics.trackOptOutAnalytics(itemSection.title, selectedVariant, 'add');
    }
 
    this.validateMissingSection(itemSection.title);
  }

  removeOptOutOfItemSection(itemSection: MealBuilderItemSection, shouldTrackAnalytics: boolean = true) {
    const optedOutSections = this.store.state.mealBuilder.optedOutItemSectionIds;
    const newlyOptedOutSections = optedOutSections.slice();
    const selectedVariant = this.store.state.mealBuilder.selectedVariant;
    if (optedOutSections.includes(itemSection.id)) {
      newlyOptedOutSections.splice(optedOutSections.indexOf(itemSection.id), 1);
      if (shouldTrackAnalytics) {
        this.mealBuilderAnalytics.trackOptOutAnalytics(itemSection.title, selectedVariant, 'remove');
      }
    }
    this.setOptedOutItemSectionIds(newlyOptedOutSections);
  }

  protected setOptedOutDefaultContentItemIds(itemIds: string[]) {
    this.store.commit(MUTATIONS.SET_OPTED_OUT_DEFAULT_CONTENT_ITEMS, itemIds);
  }

  protected setOptedOutItemSectionIds(ids: string[]) {
    this.store.commit(
      MUTATIONS.SET_OPTED_OUT_ITEM_SECTIONS,
      uniqBy(ids, i => i),
    );
  }

  protected makeNumCustomizationsRemainingMessage(variant: SelectableVariantMenuItem) {
    const numCustomizationsRemaining = variant.item.maxCustomizations - numSelectedCustomizations(this.getSelectedItems(variant.contents));

    return makeUniqueValue(`${numCustomizationsRemaining} customization${numCustomizationsRemaining == 1 ? '' : 's'} left`);
  }

  protected findCustomizationsToSatisfyItemSectionMaxQuantity(
    item: SelectableMenuItem,
    oldItem: SelectableMenuItem,
    maxQuantity: number,
  ): { newItem: MenuItemCardCustomization | null; oldItem: MenuItemCardCustomization | null } | null {
    let result: {
      contentsAfterCustomizations: number;
      oldItem: MenuItemCardCustomization | null;
      newItem: MenuItemCardCustomization | null;
    } | null = null;

    const oldItemCustomizations = [null, ...oldItem.customizations.filter(c => c.type == ItemCardCustomizationType.Customization)] as (MenuItemCardCustomization | null)[];
    const newItemCustomizations = [null, ...item.customizations.filter(c => c.type == ItemCardCustomizationType.Customization)] as (MenuItemCardCustomization | null)[];

    for (const oldItemCustomization of oldItemCustomizations) {
      for (const newItemCustomization of newItemCustomizations) {
        const oldItemCustomizationCountTowardsMax = oldItemCustomization?.customization.countTowardsContentMax ?? 0;
        const newItemCustomizationCountTowardsMax = newItemCustomization?.customization.countTowardsContentMax ?? 0;

        // 2 (for both the old & new items) + the content offsets (which will hopefully be negative)
        const contentsAfterCustomizations = 2 + oldItemCustomizationCountTowardsMax + newItemCustomizationCountTowardsMax;

        if (contentsAfterCustomizations <= maxQuantity && (!result || contentsAfterCustomizations > result.contentsAfterCustomizations)) {
          result = {
            oldItem: oldItemCustomization,
            newItem: newItemCustomization,
            contentsAfterCustomizations,
          };
        }
      }
    }

    return result;
  }

  protected getEffectiveItemsQuantity(items: SelectableMenuItem[]): number {
    return items.reduce((total, i) => total + this.getEffectiveItemQuantity(i), 0);
  }

  protected clearItemCustomization(item: SelectableMenuItem): ItemCardCustomization | undefined {
    const selectedCustomization = item.customizations.find(c => c.selected);
    if (selectedCustomization) {
      selectedCustomization.selected = false;
    }

    return selectedCustomization;
  }

  protected addNumCustomizationsRemainingMessage(item: SelectableMenuItem, variant: SelectableVariantMenuItem, oldNumSelectedCustomizations: number) {
    const numCustomizations = numSelectedCustomizations(this.getSelectedItems(variant.contents));
    if (variant.item.maxCustomizations !== -1 && numCustomizations != oldNumSelectedCustomizations) {
      item.message = this.makeNumCustomizationsRemainingMessage(variant);
    }
  }

  private selectItemBySplittingSection(
    item: SelectableMenuItem,
    otherItem: SelectableMenuItem,
    variant: SelectableVariantMenuItem,
    itemSection: MealBuilderItemSection,
    numCustomizations: number,
    args: any,
    showInformationalMessage: boolean,
    shouldTrackAnalytics: boolean,
  ): void {
    const newCustomizations = this.findCustomizationsToSatisfyItemSectionMaxQuantity(item, otherItem, itemSection.maxQuantity || 0);
    if (newCustomizations) {
      this.logger.debug('Converting to quantity customizations for item and oldItem to allow selection of item', {
        item: item.item.itemId,
        oldItem: otherItem.item.itemId,
      });

      const maxCustomizations = variant.item.maxCustomizations;
      if (maxCustomizations !== -1 && numCustomizations >= maxCustomizations) {
        this.toggleItemCustomizationSafe(item, item.customizations[0], itemSection, variant, false, shouldTrackAnalytics);
        return;
      }

      for (const op of [
        { item: otherItem, customization: newCustomizations.oldItem },
        { item, customization: newCustomizations.newItem },
      ]) {
        this.clearItemCustomization(op.item);

        if (op.customization) {
          this.toggleItemCustomizationSafe(op.item, op.customization, itemSection, variant, false, shouldTrackAnalytics);
        }
      }

      this.selectItem(item, args);

      if (variant) {
        if (showInformationalMessage) {
          this.addNumCustomizationsRemainingMessage(item, variant, numCustomizations);
        }
      }
    }
  }

  private validateMissingSection(sectionTitle: string) {
    const currentMissingSections = this.store.state.mealBuilder.missingItemSections;

    if (currentMissingSections.indexOf(sectionTitle) > -1) {
      this.store.commit(MUTATIONS.SET_MISSING_ITEM_SECTIONS, currentMissingSections.filter(x => x != sectionTitle));
    }
  }
}
